import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import {
  fetchMesures, updateMesureFn, fetchMesureWindDirOptions, fetchMesureOmmOptions,
} from '@/api/endpoints';
import type { Mesure, MesureOption, MesureUpdateInput, OmmConfig } from '@/api/types';

const AGREG_LABELS = ['NONE', 'AVG', 'SUM', 'MAX', 'MIN'] as const;

type SortKey = 'name' | 'unit' | 'json_input' | 'archive_table' | 'agreg_type';

function agregLabel(v: number): string {
  return AGREG_LABELS[v] ?? String(v);
}

export default function Mesures({ device }: { device: Device }) {
  const [sort, setSort] = useState<SortKey>('name');
  const [asc, setAsc] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Mesure | null>(null);
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'superAdmin';

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['mesures'],
    queryFn: fetchMesures,
  });
  const mesures = data?.mesures ?? [];

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = mesures.filter(
      (m) => !s ||
        m.name.toLowerCase().includes(s) ||
        (m.json_input ?? '').toLowerCase().includes(s) ||
        (m.unit ?? '').toLowerCase().includes(s),
    );
    return [...list].sort((a, b) => {
      const va = a[sort];
      const vb = b[sort];
      if (typeof va === 'number' && typeof vb === 'number') return asc ? va - vb : vb - va;
      const cmp = String(va ?? '').localeCompare(String(vb ?? ''));
      return asc ? cmp : -cmp;
    });
  }, [mesures, search, sort, asc]);

  const toggleSort = (k: SortKey) => {
    if (sort === k) setAsc((v) => !v);
    else { setSort(k); setAsc(true); }
  };

  if (isError) {
    return (
      <PageShell device={device} title="Mesures" breadcrumb="Principal">
        <div className="p-8 text-center">
          <p className="text-destructive font-medium">Erreur de chargement</p>
          <p className="text-sm text-muted-foreground mt-1">{error instanceof Error ? error.message : 'Erreur inconnue'}</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell device={device} title="Mesures" breadcrumb={isSuperAdmin ? 'Principal · superAdmin' : 'Principal · lecture seule'}>
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="search"
            placeholder="Rechercher name, json_input, unit…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[200px] max-w-md"
          />
          <span className="text-xs text-muted-foreground">
            {isLoading ? 'Chargement…' : `${filtered.length} mesures`}
          </span>
          {!isSuperAdmin && (
            <span className="text-[11px] text-muted-foreground ml-auto">🔒 édition réservée au superAdmin</span>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold w-12">id</th>
                <SortTh label="name" k="name" sort={sort} asc={asc} onClick={toggleSort} />
                <SortTh label="unit" k="unit" sort={sort} asc={asc} onClick={toggleSort} />
                <SortTh label="json_input" k="json_input" sort={sort} asc={asc} onClick={toggleSort} />
                <SortTh label="archive_table" k="archive_table" sort={sort} asc={asc} onClick={toggleSort} />
                <SortTh label="agreg_type" k="agreg_type" sort={sort} asc={asc} onClick={toggleSort} />
                <th className="text-left px-4 py-2.5 font-semibold">flags</th>
                <th className="w-10 px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  className={`cursor-pointer ${m.archive_table === 'skip' ? 'opacity-60' : ''} hover:bg-accent/30`}
                  onClick={() => setSelected(m)}
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{m.id}</td>
                  <td className="px-4 py-2.5 font-medium font-mono text-xs">{m.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{m.unit || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{m.json_input || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    {m.archive_table === 'skip'
                      ? <span className="badge-slate px-1.5 py-0.5 rounded text-[10px] font-medium">skip</span>
                      : <span className="text-muted-foreground">{m.archive_table || '—'}</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`badge-${m.agreg_type === 0 ? 'slate' : 'blue'} px-1.5 py-0.5 rounded text-[10px] font-medium`}>
                      {agregLabel(m.agreg_type)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground space-x-1">
                    {m.is_winddir && <span className="badge-violet px-1 rounded text-[10px]">winddir</span>}
                    {m.is_maxdir && <span className="badge-violet px-1 rounded text-[10px]">maxdir</span>}
                    {m.omm != null && <span className="badge-amber px-1 rounded text-[10px]">omm</span>}
                    {!m.allow_zero && <span className="text-[10px]">!allow_zero</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-muted-foreground">→</td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground text-sm">Aucune mesure.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <MesureDrawer
          mesure={selected}
          mesures={mesures}
          canEdit={isSuperAdmin}
          onClose={() => setSelected(null)}
        />
      )}
    </PageShell>
  );
}

function SortTh({ label, k, sort, asc, onClick }: { label: string; k: SortKey; sort: SortKey; asc: boolean; onClick: (k: SortKey) => void }) {
  const active = sort === k;
  return (
    <th className="text-left px-4 py-2.5 font-semibold">
      <button onClick={() => onClick(k)} className={`inline-flex items-center gap-1 hover:text-foreground ${active ? 'text-foreground' : ''}`}>
        {label}{active && <span className="text-[10px] text-primary">{asc ? '↑' : '↓'}</span>}
      </button>
    </th>
  );
}

// ---- Drawer ----------------------------------------------------------------

// OMM editor state: single object. `data` = '' means no OMM (normal/source measure).
// Each offset is '' (absent) or an integer (hours).
type OmmFormState = { data: number | ''; offset: number | ''; offset_min: number | ''; offset_max: number | '' };

function ommToForm(omm: OmmConfig | null): OmmFormState {
  return {
    data: omm?.data ?? '',
    offset: omm?.offset ?? '',
    offset_min: omm?.offset_min ?? '',
    offset_max: omm?.offset_max ?? '',
  };
}

function formToOmm(o: OmmFormState): OmmConfig | null {
  if (o.data === '') return null;
  const out: OmmConfig = { data: Number(o.data) };
  if (o.offset !== '') out.offset = Number(o.offset);
  if (o.offset_min !== '') out.offset_min = Number(o.offset_min);
  if (o.offset_max !== '') out.offset_max = Number(o.offset_max);
  return out;
}

type FormState = {
  name: string;
  unit: string;
  json_input: string;
  json_input_bis: string;
  archive_col: string;
  archive_table: string;
  field_dir: number | '';
  max: boolean;
  min: boolean;
  agreg_type: number;
  is_winddir: boolean;
  allow_zero: boolean;
  is_maxdir: boolean;
  convertText: string;
  omm: OmmFormState;
};

function initForm(m: Mesure): FormState {
  return {
    name: m.name,
    unit: m.unit ?? '',
    json_input: m.json_input ?? '',
    json_input_bis: m.json_input_bis ?? '',
    archive_col: m.archive_col ?? '',
    archive_table: m.archive_table ?? '',
    field_dir: m.field_dir ?? '',
    max: m.max,
    min: m.min,
    agreg_type: m.agreg_type,
    is_winddir: m.is_winddir,
    allow_zero: m.allow_zero,
    is_maxdir: !!m.is_maxdir,
    convertText: m.convert ? JSON.stringify(m.convert, null, 2) : '',
    omm: ommToForm(m.omm),
  };
}

function MesureDrawer({ mesure, mesures, canEdit, onClose }: { mesure: Mesure; mesures: Mesure[]; canEdit: boolean; onClose: () => void }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState<FormState>(() => initForm(mesure));
  const [convertErr, setConvertErr] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((s) => ({ ...s, [k]: v }));
  const setOmm = <K extends keyof OmmFormState>(k: K, v: OmmFormState[K]) =>
    setF((s) => ({ ...s, omm: { ...s.omm, [k]: v } }));

  const { data: ommOpts = [] } = useQuery({ queryKey: ['mesures', 'omm-options'], queryFn: fetchMesureOmmOptions, enabled: editing });
  const { data: windOpts = [] } = useQuery({ queryKey: ['mesures', 'winddir-options'], queryFn: fetchMesureWindDirOptions, enabled: editing });

  const updateMut = useConfirmedMutation(
    (patch: MesureUpdateInput) => updateMesureFn(mesure.id, patch),
    {
      title: `Modifier la mesure ${mesure.name}`,
      message: 'Confirmer la mise à jour ? Les caches ingest + mqtt seront réinitialisés.',
      onSuccess: () => {
        toast.push('Mesure modifiée · caches invalidés', 'success');
        qc.invalidateQueries({ queryKey: ['mesures'] });
        onClose();
      },
    },
  );

  const onSave = () => {
    let convert: Record<string, unknown> | null = null;
    if (f.convertText.trim()) {
      try {
        convert = JSON.parse(f.convertText);
      } catch {
        setConvertErr('JSON invalide');
        return;
      }
    }
    setConvertErr(null);
    const patch: MesureUpdateInput = {
      name: f.name,
      unit: f.unit.trim() === '' ? null : f.unit,
      json_input: f.json_input.trim() === '' ? null : f.json_input,
      json_input_bis: f.json_input_bis.trim() === '' ? null : f.json_input_bis,
      archive_col: f.archive_col.trim() === '' ? null : f.archive_col,
      archive_table: f.archive_table.trim() === '' ? null : f.archive_table,
      field_dir: f.field_dir === '' ? null : Number(f.field_dir),
      max: f.max,
      min: f.min,
      agreg_type: f.agreg_type,
      is_winddir: f.is_winddir,
      allow_zero: f.allow_zero,
      is_maxdir: f.is_maxdir,
      convert,
      omm: f.name.endsWith('_omm') ? formToOmm(f.omm) : null,
    };
    updateMut.mutate(patch);
  };

  const coherenceWarn =
    (agregLabel(f.agreg_type) === 'MAX' && !f.max) || (agregLabel(f.agreg_type) === 'MIN' && !f.min);
  // OMM set but no offset → produces nothing (non-blocking warning).
  const ommWarn =
    f.omm.data !== '' && f.omm.offset === '' && f.omm.offset_min === '' && f.omm.offset_max === '';

  const ro = !editing; // read-only
  // OMM config only applies to measures whose name ends with `_omm` (target measures).
  const isOmm = (ro ? mesure.name : f.name).endsWith('_omm');

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full md:w-[640px] bg-card border-l border-border shadow-2xl overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-xs text-muted-foreground">
              {editing ? 'Édition' : 'Détail'} · id <span className="font-mono">{mesure.id}</span>
            </div>
            <h2 className="text-lg font-bold font-mono">{mesure.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && !editing && (
              <button onClick={() => setEditing(true)} className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium">
                Modifier
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-md hover:bg-muted" aria-label="Fermer">✕</button>
          </div>
        </div>

        {editing && (
          <div className="mx-5 mt-4 p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/40 text-xs flex gap-2" role="alert">
            <span>⚠</span>
            <div><strong>Opération sensible.</strong> Toute modification réinitialise les caches ingest + mqtt et est tracée dans l’audit.</div>
          </div>
        )}

        <div className="p-5 space-y-5 flex-1">
          <Section legend="Identification">
            <Row><Lbl>name</Lbl>{ro ? <RO v={f.name} mono /> : <In v={f.name} onChange={(v) => set('name', v)} mono />}</Row>
            <Row><Lbl>unit</Lbl>{ro ? <RO v={f.unit} /> : <In v={f.unit} onChange={(v) => set('unit', v)} maxLength={20} />}</Row>
          </Section>

          <Section legend="Mapping sources">
            <Row><Lbl>json_input</Lbl>{ro ? <RO v={f.json_input} mono /> : <In v={f.json_input} onChange={(v) => set('json_input', v)} maxLength={20} mono />}</Row>
            <Row><Lbl>json_input_bis</Lbl>{ro ? <RO v={f.json_input_bis} mono /> : <In v={f.json_input_bis} onChange={(v) => set('json_input_bis', v)} maxLength={20} mono />}</Row>
            <Row><Lbl>archive_col</Lbl>{ro ? <RO v={f.archive_col} mono /> : <In v={f.archive_col} onChange={(v) => set('archive_col', v)} maxLength={20} mono />}</Row>
            <Row><Lbl>archive_table</Lbl>{ro ? <RO v={f.archive_table} mono /> : <In v={f.archive_table} onChange={(v) => set('archive_table', v)} maxLength={20} mono />}</Row>
          </Section>

          <Section legend="Agrégation & flags">
            <Row>
              <Lbl>agreg_type</Lbl>
              {ro ? <RO v={agregLabel(f.agreg_type)} /> : (
                <select value={f.agreg_type} onChange={(e) => set('agreg_type', Number(e.target.value))} className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm">
                  {AGREG_LABELS.map((lbl, i) => <option key={i} value={i}>{lbl} ({i})</option>)}
                </select>
              )}
            </Row>
            <Row>
              <Lbl>field_dir</Lbl>
              {ro ? <RO v={f.field_dir === '' ? '—' : String(f.field_dir)} mono /> : (
                <select value={f.field_dir} onChange={(e) => set('field_dir', e.target.value === '' ? '' : Number(e.target.value))} className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">— aucune —</option>
                  {windOpts.map((o: MesureOption) => <option key={o.id} value={o.id}>{o.name} (id {o.id})</option>)}
                  {f.field_dir !== '' && !windOpts.some((o) => o.id === f.field_dir) && (
                    <option value={f.field_dir}>id {f.field_dir}</option>
                  )}
                </select>
              )}
            </Row>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
              <Chk label="max" v={f.max} ro={ro} onChange={(v) => set('max', v)} />
              <Chk label="min" v={f.min} ro={ro} onChange={(v) => set('min', v)} />
              <Chk label="allow_zero" v={f.allow_zero} ro={ro} onChange={(v) => set('allow_zero', v)} />
              <Chk label="is_winddir" v={f.is_winddir} ro={ro} onChange={(v) => set('is_winddir', v)} />
              <Chk label="is_maxdir" v={f.is_maxdir} ro={ro} onChange={(v) => set('is_maxdir', v)} />
            </div>
            {!ro && coherenceWarn && (
              <div className="mt-1 p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800/40 text-xs">
                ⚠ Cohérence : <code>agreg_type={agregLabel(f.agreg_type)}</code> mais <code>{agregLabel(f.agreg_type).toLowerCase()}=false</code> (non bloquant).
              </div>
            )}
          </Section>

          {isOmm && (
          <Section legend="OMM (mesure cible → source décalée)">
            {ro ? (
              <OmmReadView omm={mesure.omm} mesures={mesures} />
            ) : (
              <div className="space-y-2">
                <Row>
                  <Lbl>data (source)</Lbl>
                  <select
                    value={f.omm.data}
                    onChange={(e) => setOmm('data', e.target.value === '' ? '' : Number(e.target.value))}
                    className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">— aucune (mesure normale) —</option>
                    {ommOpts
                      .filter((o: MesureOption) => o.id !== mesure.id)
                      .map((o: MesureOption) => <option key={o.id} value={o.id}>{o.name} ({o.id})</option>)}
                  </select>
                </Row>
                {f.omm.data !== '' && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <OmmOffset label="offset" hint="→ obs" v={f.omm.offset} onChange={(v) => setOmm('offset', v)} />
                      <OmmOffset label="offset_min" hint="→ x_min" v={f.omm.offset_min} onChange={(v) => setOmm('offset_min', v)} />
                      <OmmOffset label="offset_max" hint="→ x_max" v={f.omm.offset_max} onChange={(v) => setOmm('offset_max', v)} />
                    </div>
                    {ommWarn && (
                      <div className="p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800/40 text-xs">
                        ⚠ Au moins un offset (<code>offset</code> / <code>offset_min</code> / <code>offset_max</code>) sinon la mesure ne produit rien.
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      <code>data</code> = id mesure SOURCE. Offsets en heures (décalage bucket-jour). Champ vide = absent.
                    </p>
                  </>
                )}
              </div>
            )}
          </Section>
          )}

          <Section legend="Conversion (jsonb — lambdas Python)">
            {ro ? (
              <pre className="w-full px-3 py-2 rounded-md border border-input bg-muted text-xs font-mono whitespace-pre-wrap break-all min-h-9">
                {mesure.convert ? JSON.stringify(mesure.convert, null, 2) : '—'}
              </pre>
            ) : (
              <>
                <textarea
                  value={f.convertText}
                  onChange={(e) => set('convertText', e.target.value)}
                  rows={5}
                  placeholder='{"json": "...", "w_dump": "..."}'
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-xs font-mono"
                />
                {convertErr && <p className="text-xs text-destructive mt-1">{convertErr}</p>}
              </>
            )}
          </Section>
        </div>

        {editing && (
          <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 py-3 flex gap-2 justify-end">
            <button onClick={() => { setF(initForm(mesure)); setConvertErr(null); setEditing(false); }} className="h-9 px-4 rounded-md border border-border text-sm">
              Annuler
            </button>
            <button
              onClick={onSave}
              disabled={updateMut.isPending || !f.name.trim()}
              className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
            >
              {updateMut.isPending ? 'Sauvegarde…' : 'Enregistrer'}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

function OmmReadView({ omm, mesures }: { omm: OmmConfig | null; mesures: Mesure[] }) {
  if (!omm) return <div className="text-sm text-muted-foreground">—</div>;
  const targets: string[] = [];
  if (omm.offset !== undefined) targets.push(`offset ${omm.offset}h → obs`);
  if (omm.offset_min !== undefined) targets.push(`offset_min ${omm.offset_min}h → x_min`);
  if (omm.offset_max !== undefined) targets.push(`offset_max ${omm.offset_max}h → x_max`);
  const src = mesures.find((m) => m.id === omm.data);
  return (
    <div className="space-y-1 text-xs font-mono">
      <div>
        <span className="text-muted-foreground">data</span> = vient de{' '}
        {src ? src.name : <span className="text-amber-600 dark:text-amber-500">mesure introuvable</span>}
        {' '}(id: {omm.data})
      </div>
      {targets.length
        ? targets.map((t, i) => <div key={i} className="text-muted-foreground">{t}</div>)
        : <div className="text-amber-600 dark:text-amber-500">aucun offset — ne produit rien</div>}
    </div>
  );
}

function OmmOffset({ label, hint, v, onChange }: { label: string; hint: string; v: number | ''; onChange: (v: number | '') => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-mono">{label} <span className="text-muted-foreground">{hint}</span></span>
      <input
        type="number"
        value={v}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        placeholder="—"
        className="h-9 px-2 rounded-md border border-input bg-background text-sm font-mono"
      />
    </label>
  );
}

// ---- small field primitives ----

function Section({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset>
      <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{legend}</legend>
      <div className="space-y-2">{children}</div>
    </fieldset>
  );
}
function Row({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-3">{children}</div>;
}
function Lbl({ children }: { children: ReactNode }) {
  return <label className="w-32 shrink-0 text-sm font-medium">{children}</label>;
}
function RO({ v, mono }: { v: string | number; mono?: boolean }) {
  return (
    <div className={`flex-1 min-h-9 px-3 py-1.5 rounded-md border border-input bg-muted text-sm break-all ${mono ? 'font-mono text-xs' : ''}`}>
      {v === '' || v == null ? '—' : v}
    </div>
  );
}
function In({ v, onChange, mono, maxLength }: { v: string; onChange: (v: string) => void; mono?: boolean; maxLength?: number }) {
  return (
    <input
      value={v}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      className={`flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm ${mono ? 'font-mono' : ''}`}
    />
  );
}
function Chk({ label, v, ro, onChange }: { label: string; v: boolean; ro: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={`flex items-center gap-2 p-2 rounded-md border border-input ${ro ? 'opacity-90' : 'hover:bg-muted/50 cursor-pointer'}`}>
      <input type="checkbox" checked={v} disabled={ro} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      <span className="text-sm">{label}</span>
    </label>
  );
}
